import type { BoardBackground } from "@kandoo/shared";

export const COLOR_PRESETS = [
  "#0ea5e9",
  "#6366f1",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#ec4899",
  "#8b5cf6",
  "#334155",
];

export const GRADIENT_PRESETS: { id: string; css: string }[] = [
  { id: "sunset", css: "linear-gradient(135deg, #f97316, #ec4899)" },
  { id: "ocean", css: "linear-gradient(135deg, #0ea5e9, #6366f1)" },
  { id: "forest", css: "linear-gradient(135deg, #10b981, #0d9488)" },
  { id: "grape", css: "linear-gradient(135deg, #8b5cf6, #d946ef)" },
  { id: "dusk", css: "linear-gradient(135deg, #1e293b, #334155)" },
  { id: "synth", css: "linear-gradient(135deg, #2a1e49, #ff2e97)" },
  { id: "miami", css: "linear-gradient(135deg, #ff6ac1, #2de2e6)" },
  { id: "peach", css: "linear-gradient(135deg, #ffd86f, #fc6262)" },
  { id: "aurora", css: "linear-gradient(160deg, #00c9ff, #92fe9d)" },
  { id: "candy", css: "linear-gradient(135deg, #a18cd1, #fbc2eb)" },
  { id: "flamingo", css: "linear-gradient(135deg, #f83600, #f9d423)" },
  { id: "nebula", css: "linear-gradient(135deg, #0f0c29, #302b63, #a044ff)" },
  { id: "halo", css: "radial-gradient(circle at 30% 20%, #f9d423, #ff4e50)" },
  {
    id: "prism",
    css: "conic-gradient(from 45deg, #ff2e97, #ffcc4d, #05ffa1, #36f9f6, #8a5cff, #ff2e97)",
  },
];

/* -------------------------------------------------------------------------- */
/* Pattern overlays                                                            */
/* -------------------------------------------------------------------------- */

const svgUri = (svg: string) =>
  `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;

/**
 * Tileable translucent-white SVG overlays, layered on top of a color,
 * gradient, or image background. White at low opacity reads on any base.
 */
export const PATTERN_PRESETS: {
  id: string;
  label: string;
  svg: string;
  size: string;
}[] = [
  {
    id: "dots",
    label: "Dots",
    svg: `<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24'><circle cx='12' cy='12' r='2' fill='#fff' fill-opacity='.14'/></svg>`,
    size: "24px 24px",
  },
  {
    id: "stripes",
    label: "Stripes",
    svg: `<svg xmlns='http://www.w3.org/2000/svg' width='40' height='40'><path d='M-10 30 L30 -10 M0 40 L40 0 M10 50 L50 10' stroke='#fff' stroke-opacity='.08' stroke-width='8'/></svg>`,
    size: "40px 40px",
  },
  {
    id: "grid",
    label: "Grid",
    svg: `<svg xmlns='http://www.w3.org/2000/svg' width='32' height='32'><path d='M32 0 H0 V32' fill='none' stroke='#fff' stroke-opacity='.10'/></svg>`,
    size: "32px 32px",
  },
  {
    id: "waves",
    label: "Waves",
    svg: `<svg xmlns='http://www.w3.org/2000/svg' width='40' height='20'><path d='M0 10 Q10 0 20 10 T40 10' fill='none' stroke='#fff' stroke-opacity='.13' stroke-width='2'/></svg>`,
    size: "40px 20px",
  },
  {
    id: "zigzag",
    label: "Zigzag",
    svg: `<svg xmlns='http://www.w3.org/2000/svg' width='24' height='14'><path d='M0 12 L6 2 L12 12 L18 2 L24 12' fill='none' stroke='#fff' stroke-opacity='.10' stroke-width='2'/></svg>`,
    size: "24px 14px",
  },
  {
    id: "bubbles",
    label: "Bubbles",
    svg: `<svg xmlns='http://www.w3.org/2000/svg' width='96' height='96'><g fill='#fff'><circle cx='18' cy='22' r='9' fill-opacity='.09'/><circle cx='70' cy='14' r='5' fill-opacity='.12'/><circle cx='84' cy='58' r='12' fill-opacity='.07'/><circle cx='40' cy='72' r='7' fill-opacity='.11'/><circle cx='8' cy='84' r='4' fill-opacity='.13'/><circle cx='56' cy='42' r='3' fill-opacity='.14'/></g></svg>`,
    size: "96px 96px",
  },
  {
    id: "confetti",
    label: "Confetti",
    svg: `<svg xmlns='http://www.w3.org/2000/svg' width='64' height='64'><g fill='#fff' fill-opacity='.12'><rect x='8' y='10' width='6' height='6' transform='rotate(20 11 13)'/><rect x='44' y='30' width='6' height='6' transform='rotate(-30 47 33)'/><circle cx='30' cy='50' r='3'/><circle cx='54' cy='8' r='2.5'/><path d='M14 40 l5 9 h-10 z'/><path d='M38 12 l4 7 h-8 z' transform='rotate(40 38 15)'/></g></svg>`,
    size: "64px 64px",
  },
];

/** Preview style for a pattern swatch (pattern over a neutral base). */
export function patternPreviewStyle(id: string): React.CSSProperties {
  const p = PATTERN_PRESETS.find((x) => x.id === id);
  if (!p) return {};
  return {
    backgroundColor: "#64748b",
    backgroundImage: svgUri(p.svg),
    backgroundSize: p.size,
  };
}

/** Max inline image size (~1.5 MB of data URI) to keep board payloads sane. */
export const MAX_BG_IMAGE_BYTES = 1_500_000;

/** CSS style object for a board background (empty when none). An overlay
 *  pattern rides on top as an extra background-image layer. */
export function backgroundStyle(
  bg: BoardBackground | null,
): React.CSSProperties {
  if (!bg) return {};
  const pattern = bg.pattern
    ? PATTERN_PRESETS.find((p) => p.id === bg.pattern)
    : undefined;
  const layer = pattern ? svgUri(pattern.svg) : null;

  if (bg.type === "color") {
    return {
      backgroundColor: bg.value,
      ...(pattern && layer
        ? { backgroundImage: layer, backgroundSize: pattern.size }
        : {}),
    };
  }
  if (bg.type === "gradient") {
    return pattern && layer
      ? {
          backgroundImage: `${layer}, ${bg.value}`,
          backgroundSize: `${pattern.size}, auto`,
        }
      : { backgroundImage: bg.value };
  }
  return pattern && layer
    ? {
        backgroundImage: `${layer}, url(${bg.value})`,
        backgroundSize: `${pattern.size}, cover`,
        backgroundPosition: "0 0, center",
      }
    : {
        backgroundImage: `url(${bg.value})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      };
}
